/**
 * SecurityPanel — OptiGuard Security Tab for the Auditor Hub Modal
 *
 * Left column: sub-agent list with severity counts
 * Right: findings list for selected sub-agent, with status actions
 * Bottom: chat with Security Coordinator (Qwen 32B)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import './SecurityPanel.css';

const OPTIGUARD_URL = 'http://localhost:5690';

const SUB_AGENTS = [
  { id: null,                 label: '🛡️ All Findings' },
  { id: 'dependency_auditor', label: '📦 Dependency Auditor' },
  { id: 'secret_scanner',     label: '🔑 Secret Scanner' },
  { id: 'auth_sentinel',      label: '🔐 Auth Sentinel' },
  { id: 'endpoint_monitor',   label: '🌐 Endpoint Monitor' },
  { id: 'config_compliance',  label: '⚙️ Config Compliance' },
];

const SEVERITY_COLOR = {
  critical: '#dc2626',
  high:     '#ea580c',
  medium:   '#d97706',
  low:      '#65a30d',
  info:     '#0891b2',
};

const STATUS_LABELS = {
  open:          '🔴 Open',
  acknowledged:  '🟡 Acknowledged',
  resolved:      '✅ Resolved',
  false_positive:'⚪ False Positive',
};

export default function SecurityPanel() {
  const [latestReport, setLatestReport] = useState(null);
  const [findings, setFindings] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [statusFilter, setStatusFilter] = useState('open');
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);

  // Chat
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  const fetchFindings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (selectedAgent) params.set('sub_agent', selectedAgent);
      if (statusFilter) params.set('status', statusFilter);

      const res = await fetch(`${OPTIGUARD_URL}/api/security/findings?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setFindings(data.findings || []);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [selectedAgent, statusFilter]);

  const fetchLatestReport = useCallback(async () => {
    try {
      const res = await fetch(`${OPTIGUARD_URL}/api/security/latest-report`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setLatestReport(data.scan);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    fetchLatestReport();
  }, [fetchLatestReport]);

  useEffect(() => {
    fetchFindings();
  }, [fetchFindings]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  async function triggerScan() {
    setScanning(true);
    try {
      await fetch(`${OPTIGUARD_URL}/api/security/run`, { method: 'POST' });
      // Poll for completion
      setTimeout(async () => {
        await fetchLatestReport();
        await fetchFindings();
        setScanning(false);
      }, 45000);
    } catch {
      setScanning(false);
    }
  }

  async function updateFindingStatus(findingId, status, adminNotes) {
    try {
      const res = await fetch(`${OPTIGUARD_URL}/api/security/findings/${findingId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, admin_notes: adminNotes }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchFindings();
      setSelectedFinding(null);
    } catch (err) {
      alert(`Failed to update: ${err.message}`);
    }
  }

  async function sendChat() {
    if (!chatInput.trim()) return;
    const userMsg = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setChatLoading(true);

    try {
      const res = await fetch(`${OPTIGUARD_URL}/api/security/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMsg }),
      });
      const data = await res.json();
      setChatMessages(prev => [...prev, { role: 'assistant', content: data.response || data.error }]);
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err.message}` }]);
    }
    setChatLoading(false);
  }

  // Counts for selected agent
  const agentCounts = {};
  for (const f of findings) {
    const key = f.severity;
    agentCounts[key] = (agentCounts[key] || 0) + 1;
  }

  const scanDate = latestReport
    ? new Date(latestReport.started_at).toLocaleString('en-CA', { dateStyle: 'short', timeStyle: 'short' })
    : null;

  return (
    <div className="security-panel">
      {/* ── Top bar ── */}
      <div className="security-topbar">
        <div className="security-topbar-left">
          <span className="security-service-label">🛡️ OptiGuard</span>
          {latestReport && (
            <span className="security-scan-meta">
              Last scan: {scanDate}
              {' \u00B7 '}
              <span style={{ color: SEVERITY_COLOR.critical }}>{latestReport.critical_count} critical</span>
              {' \u00A0'}
              <span style={{ color: SEVERITY_COLOR.high }}>{latestReport.high_count} high</span>
              {' \u00A0'}
              <span style={{ color: SEVERITY_COLOR.medium }}>{latestReport.medium_count} medium</span>
            </span>
          )}
        </div>
        <div className="security-topbar-right">
          <select
            className="security-status-filter"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="open">Open</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="resolved">Resolved</option>
            <option value="false_positive">False Positive</option>
          </select>
          <button
            className="security-scan-btn"
            onClick={triggerScan}
            disabled={scanning}
          >
            {scanning ? '⏳ Scanning…' : '▶ Run Scan'}
          </button>
        </div>
      </div>

      {/* ── Main content: left + center + right ── */}
      <div className="security-body">
        {/* Left: sub-agent list */}
        <div className="security-agents-col">
          {SUB_AGENTS.map(agent => {
            const agentFindings = agent.id
              ? findings.filter(f => f.sub_agent === agent.id)
              : findings;
            const critCount = agentFindings.filter(f => f.severity === 'critical').length;
            const highCount = agentFindings.filter(f => f.severity === 'high').length;
            const isActive = selectedAgent === agent.id;

            return (
              <button
                key={agent.id || 'all'}
                className={`security-agent-btn ${isActive ? 'active' : ''}`}
                onClick={() => { setSelectedAgent(agent.id); setSelectedFinding(null); }}
              >
                <span className="security-agent-label">{agent.label}</span>
                <span className="security-agent-badges">
                  {critCount > 0 && <span className="security-badge critical">{critCount}</span>}
                  {highCount > 0 && <span className="security-badge high">{highCount}</span>}
                </span>
              </button>
            );
          })}
        </div>

        {/* Center: findings list */}
        <div className="security-findings-col">
          {error && <div className="security-error">⚠️ {error}</div>}
          {loading ? (
            <div className="security-loading">Loading findings…</div>
          ) : findings.length === 0 ? (
            <div className="security-empty">✅ No findings for this filter.</div>
          ) : (
            findings.map(finding => (
              <button
                key={finding.id}
                className={`security-finding-row ${selectedFinding?.id === finding.id ? 'selected' : ''}`}
                onClick={() => setSelectedFinding(finding)}
              >
                <span
                  className="security-finding-sev"
                  style={{ background: SEVERITY_COLOR[finding.severity] || '#64748b' }}
                >
                  {finding.severity}
                </span>
                <span className="security-finding-title">{finding.title}</span>
                <span className="security-finding-status">{STATUS_LABELS[finding.status] || finding.status}</span>
              </button>
            ))
          )}
        </div>

        {/* Right: finding detail or chat */}
        <div className="security-detail-col">
          {selectedFinding ? (
            <FindingDetail
              finding={selectedFinding}
              onClose={() => setSelectedFinding(null)}
              onStatusUpdate={updateFindingStatus}
            />
          ) : (
            <SecurityChat
              messages={chatMessages}
              input={chatInput}
              loading={chatLoading}
              chatEndRef={chatEndRef}
              onInputChange={setChatInput}
              onSend={sendChat}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function FindingDetail({ finding, onClose, onStatusUpdate }) {
  const [adminNotes, setAdminNotes] = useState(finding.admin_notes || '');

  return (
    <div className="security-finding-detail">
      <div className="security-detail-header">
        <button className="security-detail-back" onClick={onClose}>← Back</button>
        <span
          className="security-badge"
          style={{ background: SEVERITY_COLOR[finding.severity] || '#64748b', padding: '3px 10px', borderRadius: 4, color: '#fff', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}
        >
          {finding.severity}
        </span>
      </div>

      <h3 className="security-detail-title">{finding.title}</h3>

      <div className="security-detail-meta">
        <span>Sub-agent: <strong>{finding.sub_agent}</strong></span>
        <span>Status: <strong>{STATUS_LABELS[finding.status] || finding.status}</strong></span>
        {finding.file_path && <span>File: <code>{finding.file_path}{finding.line_number ? ':' + finding.line_number : ''}</code></span>}
        <span>First seen: {new Date(finding.first_seen_at).toLocaleDateString('en-CA')}</span>
        <span>Last seen: {new Date(finding.last_seen_at).toLocaleDateString('en-CA')}</span>
      </div>

      <pre className="security-detail-desc">{finding.description}</pre>

      <div className="security-detail-notes">
        <label>Admin Notes</label>
        <textarea
          className="security-notes-textarea"
          value={adminNotes}
          onChange={e => setAdminNotes(e.target.value)}
          placeholder="Add notes about this finding…"
          rows={3}
        />
      </div>

      <div className="security-detail-actions">
        <button onClick={() => onStatusUpdate(finding.id, 'acknowledged', adminNotes)} className="security-action-btn acknowledge">Acknowledge</button>
        <button onClick={() => onStatusUpdate(finding.id, 'resolved', adminNotes)} className="security-action-btn resolve">Mark Resolved</button>
        <button onClick={() => onStatusUpdate(finding.id, 'false_positive', adminNotes)} className="security-action-btn fp">False Positive</button>
        {finding.status !== 'open' && (
          <button onClick={() => onStatusUpdate(finding.id, 'open', adminNotes)} className="security-action-btn reopen">Reopen</button>
        )}
      </div>
    </div>
  );
}

function SecurityChat({ messages, input, loading, chatEndRef, onInputChange, onSend }) {
  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  }

  return (
    <div className="security-chat">
      <div className="security-chat-header">🤖 Security Coordinator (Qwen 32B)</div>
      <div className="security-chat-messages">
        {messages.length === 0 && (
          <div className="security-chat-hint">
            Ask about security findings:<br />
            <em>"What are the most urgent issues?"</em><br />
            <em>"Summarize auth events from last week"</em><br />
            <em>"How do I fix plain text passwords?"</em>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`security-chat-msg ${msg.role}`}>
            <span className="security-chat-role">{msg.role === 'user' ? 'You' : 'Security AI'}</span>
            <pre className="security-chat-content">{msg.content}</pre>
          </div>
        ))}
        {loading && <div className="security-chat-msg assistant"><span className="security-chat-role">Security AI</span><pre className="security-chat-content">Analyzing…</pre></div>}
        <div ref={chatEndRef} />
      </div>
      <div className="security-chat-input-row">
        <textarea
          className="security-chat-input"
          value={input}
          onChange={e => onInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask the Security Coordinator…"
          rows={2}
          disabled={loading}
        />
        <button className="security-chat-send" onClick={onSend} disabled={loading || !input.trim()}>
          ↑
        </button>
      </div>
    </div>
  );
}
